import { api } from '@/lib/api';
import type {
  TemplateResponse,
  TemplateListResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateQuery,
  TemplateStatus,
  TemplatePreviewResponse,
  TemplateVersionResponse,
  TemplateVersionQuery,
  GenerateHtmlResponse,
} from '@troia-v3/types';

/**
 * Template Service
 * Serviço para gerenciar templates de mensagens
 */
export const templateService = {
  /**
   * Listar templates com filtros e paginação
   */
  async getAll(query?: TemplateQuery): Promise<TemplateListResponse> {
    const response = await api.get('/templates', { params: query });
    return response.data.data;
  },

  /**
   * Obter template por ID
   */
  async getById(id: string): Promise<TemplateResponse> {
    const response = await api.get(`/templates/${id}`);
    return response.data.data;
  },

  /**
   * Criar novo template
   */
  async create(data: CreateTemplateRequest): Promise<TemplateResponse> {
    const response = await api.post('/templates', data);
    return response.data.data;
  },

  /**
   * Atualizar template existente
   */
  async update(id: string, data: UpdateTemplateRequest): Promise<TemplateResponse> {
    const response = await api.put(`/templates/${id}`, data);
    return response.data.data;
  },

  /**
   * Deletar template
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/templates/${id}`);
  },

  /**
   * Atualizar status do template
   */
  async updateStatus(
    id: string,
    status: TemplateStatus,
    metadata?: any
  ): Promise<TemplateResponse> {
    const response = await api.patch(`/templates/${id}/status`, {
      status,
      metadata,
    });
    return response.data.data;
  },

  /**
   * Duplicar template
   */
  async duplicate(id: string): Promise<TemplateResponse> {
    const response = await api.post(`/templates/${id}/duplicate`);
    return response.data.data;
  },

  /**
   * Pré-visualizar template com dados de exemplo
   */
  async preview(
    id: string,
    sampleData: Record<number, string | number | boolean>
  ): Promise<TemplatePreviewResponse> {
    const response = await api.post(`/templates/${id}/preview`, { sampleData });
    return response.data.data;
  },

  /**
   * Buscar templates por status
   */
  async getByStatus(status: TemplateStatus): Promise<TemplateResponse[]> {
    const response = await api.get(`/templates/status/${status}`);
    return response.data.data;
  },

  /**
   * Buscar templates por provider
   */
  async getByProvider(providerId: string): Promise<TemplateResponse[]> {
    const response = await api.get(`/templates/provider/${providerId}`);
    return response.data.data;
  },

  /**
   * Verificar se template pode ser deletado
   */
  async canDelete(id: string): Promise<{ canDelete: boolean; reason?: string }> {
    const response = await api.get(`/templates/${id}/can-delete`);
    return response.data.data;
  },

  /**
   * Obter templates para dropdown (dados mínimos)
   * Retorna apenas templates aprovados
   * Opcional: filtrar por channelId
   */
  async getForDropdown(channelId?: string): Promise<Array<{ id: string; name: string; channelId: string }>> {
    const params = channelId ? { channelId } : {};
    const response = await api.get('/templates/dropdown', { params });
    return response.data.data;
  },

  /**
   * Obter histórico de versões do template
   */
  async getVersions(
    templateId: string,
    query?: TemplateVersionQuery
  ): Promise<{ items: TemplateVersionResponse[]; total: number }> {
    const response = await api.get(`/templates/${templateId}/versions`, { params: query });
    return response.data.data;
  },

  /**
   * Gerar HTML via IA baseado em instruções
   * Se templateId é fornecido, usa o endpoint específico; senão usa o geral
   */
  async generateHtml(
    data: { instructions: string; currentHtml: string },
    templateId?: string
  ): Promise<GenerateHtmlResponse> {
    const url = templateId ? `/templates/${templateId}/generate-html` : '/templates/generate-html';
    const response = await api.post(url, data);
    return response.data.data;
  },
};
